import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Decode JWT payload without a library — reads the exp field
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const expiryTimerRef = useRef(null);

  // Schedule a logout at token expiry time
  const scheduleExpiry = (token) => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    const expiry = getTokenExpiry(token);
    if (!expiry) return;
    const msUntilExpiry = expiry - Date.now();
    if (msUntilExpiry <= 0) {
      handleSessionExpired();
      return;
    }
    expiryTimerRef.current = setTimeout(handleSessionExpired, msUntilExpiry);
  };

  const handleSessionExpired = () => {
    localStorage.removeItem('token');
    localStorage.setItem('session_expired', '1');
    setUser(null);
    setLoading(false);
    window.location.href = '/login';
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Check if already expired before even fetching
      const expiry = getTokenExpiry(token);
      if (expiry && expiry < Date.now()) {
        handleSessionExpired();
        return;
      }
      fetchCurrentUser();
      scheduleExpiry(token);
    } else {
      setLoading(false);
    }

    // Re-check expiry when user returns to the tab after being away
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const t = localStorage.getItem('token');
      if (!t) return;
      const expiry = getTokenExpiry(t);
      if (expiry && expiry < Date.now()) {
        handleSessionExpired();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const data = response.data.data;
      setUser({ ...data, isAdmin: data.isAdmin || false, emailVerified: data.emailVerified || false });
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user } = response.data.data;
    localStorage.setItem('token', token);
    localStorage.removeItem('session_expired');
    setUser(user);
    scheduleExpiry(token);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    const { token, user } = response.data.data;
    localStorage.setItem('token', token);
    setUser(user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('session_expired');
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    setUser(null);
  };

  const updateUser = (updatedFields) => {
    setUser(prev => ({ ...prev, ...updatedFields }));
  };

  // Swap in a token the server re-issued (after a password change the old one
  // is revoked server-side), and re-arm the expiry timer for the new token.
  const replaceToken = (token) => {
    if (!token) return;
    localStorage.setItem('token', token);
    scheduleExpiry(token);
  };

  const value = {
    user,
    login,
    register,
    logout,
    updateUser,
    replaceToken,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
